import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { List } from 'antd';
import SessionStatusBadge from 'components/Lectures/SessionStatusBadge';
import Spinner from 'components/Spinner';
import { useSession } from 'hooks/Lectures/useSession';
import { Button, Title } from 'ui';
import moment from 'utils/momentWithLocales';
import {
    LectureProps,
    LectureSessionFilterType,
    LectureSessionType,
    SessionLectorProps,
    SessionProps,
} from '../types';
import './styles.scss';
import { useLocaleContext } from 'context/LocaleContext';

export interface MySessionsListProps {
    type: LectureSessionFilterType;
    lecture?: LectureProps;
    reload?: boolean;
    adminView?: boolean;
    userTypeId?: number;
    setAdminSession?: React.Dispatch<React.SetStateAction<number>>;
    setAdminLecture?: React.Dispatch<React.SetStateAction<number>>;
    setAdminView?: React.Dispatch<React.SetStateAction<string>>;
    calendarDate?: string;

}

const MySessionsList: React.FC<MySessionsListProps> = ({
    type,
    lecture,
    reload,
    adminView,
    userTypeId,
    setAdminSession,
    setAdminLecture,
    setAdminView,
    calendarDate,
}) => {
    const { locale } = useLocaleContext();

    const [listData, setListData] = useState<SessionProps[]>([]);
    const { getMySessions, sessionLoading, sessionCalendarLoading } = useSession();
    const navigate = useNavigate();
    const intl = useIntl();
    const params = useParams();
    const tab = params.tab;

    moment.locale(locale ?? 'en');

    useEffect(() => {
        loadMySessions();
    }, []);

    const loadMySessions = useCallback(async () => {
        const data = await getMySessions(type, lecture?.id, adminView, userTypeId);
        setListData(data);
    }, [type, lecture?.id, adminView, userTypeId]);

    const renderButton = (session: SessionProps) => {
        if (!session?.access) {
            return Boolean(session.lecturesId && session.id);
        }

        if (type === LectureSessionFilterType.Past) {
            return session.access.includes('1');
        }

        return Boolean(session.lecturesId && session.id);
    };

    const ListItem: React.FC<{ session: SessionProps }> = ({ session }) => {
        const date = moment(session.date);

        const renderType = (type?: LectureSessionType) => {
            if (!type) {
                return '';
            }

            switch (type) {
                case LectureSessionType.Online:
                    return <FormattedMessage id="general.lectures.online" />;
                case LectureSessionType.ClassRoom:
                    return <FormattedMessage id="general.lectures.classroom" />;
            }
        };

        return (
            <div className="my-session-list-item">
                <div className="my-session-list-item--head">
                    <div>
                        <span>{date.format('ddd')}</span>
                        <span>{date.format('DD')}</span>
                    </div>
                    <div>
                        <SessionStatusBadge status={session.status} />
                    </div>
                </div>
                <div className="my-session-list-item--info">
                    <div>{session.title}</div>
                    <div>
                        <span>
                            {session.start?.slice(0, -3)} - {session.end?.slice(0, -3)}
                        </span>
                        <span>{renderType(session.type)}</span>
                        <span>
                            {session.lectors
                                ?.map((lector: SessionLectorProps) =>
                                    !!lector.name ? lector.name : '---'
                                )
                                .join(', ')}
                        </span>
                    </div>
                </div>
                <div className="my-session-list-item--status">
                    <SessionStatusBadge status={session.status} />
                </div>
                <div className="my-session-list-item--cta">
                    {renderButton(session) && (
                        <Button
                            block
                            onClick={() => {
                                if (
                                    adminView &&
                                    setAdminSession &&
                                    setAdminLecture &&
                                    setAdminView &&
                                    session?.id &&
                                    session?.lecturesId
                                ) {
                                    setAdminLecture(session.lecturesId);
                                    setAdminSession(session.id);
                                    setAdminView('SESSION');
                                } else {
                                    navigate(
                                        `/my-lectures/${session.lecturesId}/session/${session.id}` + (tab ? ('/' + tab) : '') + (calendarDate ? ('/' + calendarDate) : '')
                                    );
                                }
                            }}
                            message={'general.view'}
                        />
                    )}
                </div>
            </div>
        );
    };

    type MySessionsListRow =
        | { kind: 'month'; key: string; label: string }
        | { kind: 'session'; key: string; session: SessionProps };

    const groupedListData: MySessionsListRow[] = useMemo(() => {
        const rows: MySessionsListRow[] = [];
        let lastMonthKey: string | null = null;

        listData.forEach((session: SessionProps) => {
            const monthKey = session.date ? moment(session.date).format('YYYY-MM') : 'unknown';
            if (monthKey !== lastMonthKey) {
                const label = monthKey === 'unknown' ? '---' : moment(session.date).format('MMMM YYYY');

                rows.push({
                    kind: 'month',
                    key: `month-${monthKey}`,
                    label,
                });
                lastMonthKey = monthKey;
            }

            rows.push({
                kind: 'session',
                key: `session-${session.id ?? `${monthKey}-${rows.length}`}`,
                session,
            });
        });

        return rows;
    }, [intl, listData]);

    return (
        <Spinner spinning={sessionLoading || sessionCalendarLoading}>
            <Title
                withLine
                className="my-session-calendar-title"
                title={intl.formatMessage({ id: 'general.sessions' })}
            />
            <List
                dataSource={groupedListData}
                renderItem={(row) => {
                    if (row.kind === 'month') {
                        return <div className="my-session-list-month">{row.label}</div>;
                    }

                    return <ListItem session={row.session} />;
                }}
                rowKey={(row) => row.key}
            />
        </Spinner>
    );
};

export default MySessionsList;
